const VAULT_ACTOR_JWT_STORAGE_KEY = "jwtToken";
const VAULT_ACTOR_FOLDER_STORAGE_KEY = "vaultActorFolderId";
const VAULT_STAFF_ACTOR_ID_STORAGE_KEY = "vaultStaffActorId";
const VAULT_DEMO_MODE_PREFIX = "vaultDemoMode_";
const VAULT_UPLOAD_RECOVERY_SCOPE_KEY = "vaultUploadRecoveryScope";
const VAULT_FOLDER_ID_PATHNAME_REGEX = /\/vault\/([a-f0-9-]{36})/i;
const LEGACY_STAFF_UPLOAD_RECOVERY_SCOPE = "staff:session";

export function getStoredVaultActorToken() {
  try {
    return sessionStorage.getItem(VAULT_ACTOR_JWT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getStoredVaultActorFolderId() {
  try {
    return sessionStorage.getItem(VAULT_ACTOR_FOLDER_STORAGE_KEY);
  } catch {
    return null;
  }
}

function getVaultUploadOwnerId(): string {
  let ownerId = sessionStorage.getItem(VAULT_UPLOAD_RECOVERY_SCOPE_KEY);
  if (!ownerId) {
    ownerId = crypto.randomUUID();
    sessionStorage.setItem(VAULT_UPLOAD_RECOVERY_SCOPE_KEY, ownerId);
  }
  return ownerId;
}

function getStableVaultActorIdentity(
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
): string {
  const folderId = getStoredVaultActorFolderId();
  const routedFolderId = getDemoFolderIdFromPathname(pathname);
  const token = getStoredVaultActorToken();
  const staffActorId = sessionStorage.getItem(VAULT_STAFF_ACTOR_ID_STORAGE_KEY);
  if (
    token &&
    folderId &&
    routedFolderId &&
    folderId.toLowerCase() === routedFolderId
  ) {
    const payload = decodeVaultJwtPayload(token);
    return payload
      ? `${folderId}:${payload.actor ?? "actor"}:${payload.email ?? ""}`
      : `${folderId}:actor`;
  }
  return `staff:${staffActorId ? `user:${staffActorId}` : "session"}`;
}

export function getVaultUploadRecoveryScope(): string {
  return getStableVaultActorIdentity();
}

export function getVaultUploadRecoveryOwnerId(): string {
  return getVaultUploadOwnerId();
}

export function getVaultUploadRecoveryContext(): {
  readonly scope: string;
  readonly ownerId: string;
} {
  return {
    scope: getStableVaultActorIdentity(),
    ownerId: getVaultUploadOwnerId(),
  };
}

export function setStoredVaultActorSession(folderId: string, jwtToken: string) {
  sessionStorage.setItem(VAULT_ACTOR_JWT_STORAGE_KEY, jwtToken);
  sessionStorage.setItem(VAULT_ACTOR_FOLDER_STORAGE_KEY, folderId);
}

export function getLegacyStaffUploadRecoveryScope(
  recoveryScope: string,
): string | null {
  return recoveryScope.startsWith("staff:user:")
    ? LEGACY_STAFF_UPLOAD_RECOVERY_SCOPE
    : null;
}

export function setStoredStaffActorIdentity(userId: string) {
  try {
    sessionStorage.setItem(VAULT_STAFF_ACTOR_ID_STORAGE_KEY, userId);
  } catch {
    // Staff identity persistence must not turn a successful login into a failure.
  }
}

export function clearStoredVaultActorSession() {
  sessionStorage.removeItem(VAULT_ACTOR_JWT_STORAGE_KEY);
  sessionStorage.removeItem(VAULT_ACTOR_FOLDER_STORAGE_KEY);
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(VAULT_DEMO_MODE_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
}

export function clearStoredStaffActorIdentity() {
  sessionStorage.removeItem(VAULT_STAFF_ACTOR_ID_STORAGE_KEY);
  sessionStorage.removeItem(VAULT_UPLOAD_RECOVERY_SCOPE_KEY);
}

export function hasStoredVaultActorSessionForFolder(
  folderId: string | undefined,
) {
  if (!folderId) return false;

  const storedToken = getStoredVaultActorToken();
  const storedFolderId = getStoredVaultActorFolderId();

  return Boolean(storedToken && storedFolderId === folderId);
}

export function setDemoMode(folderId: string, value: boolean): void {
  if (!folderId) return;
  const key = `${VAULT_DEMO_MODE_PREFIX}${folderId.toLowerCase()}`;
  if (value) {
    sessionStorage.setItem(key, "true");
  } else {
    sessionStorage.removeItem(key);
  }
}

export function isDemoModeActive(folderId: string | null | undefined): boolean {
  if (!folderId) return false;
  return (
    sessionStorage.getItem(
      `${VAULT_DEMO_MODE_PREFIX}${folderId.toLowerCase()}`,
    ) === "true"
  );
}

export function getDemoFolderIdFromPathname(pathname: string): string | null {
  const match = pathname.match(VAULT_FOLDER_ID_PATHNAME_REGEX);
  return match ? match[1].toLowerCase() : null;
}

interface VaultActorJwtPayload {
  readonly folder_id?: unknown;
  readonly actor?: unknown;
  readonly email?: unknown;
}

export function getVaultActorAuthScope(
  token = getStoredVaultActorToken(),
): string {
  if (!token) return "anonymous";
  const payload = decodeVaultJwtPayload(token);
  if (!payload) return "anonymous";

  const folderId =
    typeof payload.folder_id === "string" ? payload.folder_id : "unknown";
  const actor = typeof payload.actor === "string" ? payload.actor : "actor";
  return `actor:${folderId.toLowerCase()}:${actor.toLowerCase()}`;
}

export function decodeVaultJwtPayload(
  token: string | null,
): VaultActorJwtPayload | null {
  if (!token) return null;
  const parts = token.split(".");
  const encodedPayload = parts[1];
  if (!encodedPayload) return null;

  try {
    const normalized = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const parsed: unknown = JSON.parse(atob(padded));
    if (!isJwtPayloadRecord(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function isJwtPayloadRecord(value: unknown): value is VaultActorJwtPayload {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
